'use client'

import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { FileText, Send } from 'lucide-react'
import { z } from 'zod/v4'

import {
  Avatar,
  AvatarFallback,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  Textarea,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { Student } from '../types'
import { useSharedTRPC } from '../../../context'

/** Mirrors `MAX_PARENT_MESSAGE_LENGTH` on the router, so the counter and the server agree. */
const MAX_MESSAGE_LENGTH = 1000

const FormSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1, { message: 'Pesan tidak boleh kosong.' })
    .max(MAX_MESSAGE_LENGTH, {
      message: `Maksimal ${MAX_MESSAGE_LENGTH} karakter.`,
    }),
})

interface ContactParentDialogProps {
  student: Student
  onClose: () => void
}

/**
 * Ready-made openings, keyed to the four things a school actually writes home about.
 *
 * Prefill only — picking one replaces the textarea's contents and nothing else is recorded, so a
 * teacher may edit the text past recognition and the notification still renders correctly. The
 * student's name is interpolated here rather than server-side for the same reason: by send time the
 * text is just a string.
 */
const MESSAGE_TEMPLATES = [
  {
    label: '🔔 Ingatkan anak untuk login',
    build: (name: string) =>
      `Bapak/Ibu, sudah beberapa hari ${name} belum buka PiBo. Yuk, diingatkan untuk login dan lanjut membaca hari ini 😊`,
  },
  {
    label: '📖 Ajak dukung baca & belajar',
    build: (name: string) =>
      `Halo, Bapak/Ibu! Dukungan kecil di rumah bisa membuat belajar jadi lebih menyenangkan. Yuk, bantu ${name} meluangkan waktu untuk membaca dan belajar di PiBo secara rutin. 📚`,
  },
  {
    label: '📝 Ingatkan tugas',
    build: (name: string) =>
      `Halo, Bapak/Ibu! ${name} masih punya bacaan atau tugas di PiBo yang belum selesai. Mohon bantu ingatkan untuk menyelesaikannya, ya! 😊`,
  },
  {
    label: '🎉 Rayakan pencapaian',
    build: (name: string) =>
      `Wah, luar biasa! ${name} berhasil mencapai target membaca minggu ini! 🎉 Terima kasih sudah ikut mendukung kebiasaan membacanya di rumah. Yuk, beri apresiasi dan semangat untuk lanjut membaca!`,
  },
] as const

/**
 * Writes to the parent a student is already connected to.
 *
 * The recipient is never named by this component: the mutation takes `childId` and resolves the
 * family owner server-side. Everything shown here about the parent comes from the roster row's
 * `connected` arm, which carries a name and an address but no avatar — hence initials only.
 */
export function ContactParentDialog({ student, onClose }: ContactParentDialogProps) {
  const trpc = useSharedTRPC()
  const sendMessage = useMutation(trpc.homeConnect.messageParent.mutationOptions())

  const connection = student.parentConnection
  const parentLabel =
    connection.status === 'connected' ? (connection.parentName ?? connection.parentEmail ?? 'Orang tua') : 'Orang tua'

  const form = useForm({
    defaultValues: { message: '' } as z.input<typeof FormSchema>,
    validators: { onSubmit: FormSchema },
    onSubmit: async ({ value }) => {
      try {
        await sendMessage.mutateAsync({ childId: student.id, message: value.message })
        toast.success(`Pesan berhasil terkirim ke orang tua ${student.name}.`)
        onClose()
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Gagal mengirim pesan')
      }
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className='srd:flex srd:max-h-[95dvh] srd:w-full srd:max-w-lg srd:flex-col srd:overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Hubungi orang tua</DialogTitle>
          <DialogDescription>Kirim pesan ke orang tua untuk mendukung pembelajaran di rumah.</DialogDescription>
        </DialogHeader>

        <div className='srd:flex srd:flex-nowrap srd:items-center srd:gap-x-2'>
          <Avatar profile='user' size='lg'>
            <AvatarFallback>{getInitials(parentLabel)}</AvatarFallback>
          </Avatar>
          <span className='srd:flex srd:flex-col'>
            <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
              {parentLabel}
            </span>
            <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>
              Orang tua <span className='srd:font-bold'>{student.name}</span>
            </span>
          </span>
        </div>

        <form
          className='srd:flex srd:flex-col srd:gap-4'
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <FieldSet disabled={sendMessage.isPending}>
            <form.Field name='message'>
              {(field) => (
                <Field>
                  <div className='srd:flex srd:items-center srd:justify-between srd:gap-2'>
                    <FieldLabel>Pesan</FieldLabel>

                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button type='button' variant='ghost' size='sm' disabled={sendMessage.isPending}>
                            <FileText className='srd:mr-2 srd:h-4 srd:w-4' />
                            Gunakan templat
                          </Button>
                        }
                      />

                      <DropdownMenuContent align='end' className='srd:w-fit srd:max-w-[280px]'>
                        {MESSAGE_TEMPLATES.map((template) => (
                          <DropdownMenuItem
                            key={template.label}
                            onClick={() => field.handleChange(template.build(student.name))}
                          >
                            {template.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <Textarea
                    rows={6}
                    maxLength={MAX_MESSAGE_LENGTH}
                    placeholder='Tulis pesan atau gunakan templat, lalu sesuaikan untuk orang tua...'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className='srd:h-[240px]'
                  />
                  {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )}
            </form.Field>
          </FieldSet>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose} disabled={sendMessage.isPending}>
              Batal
            </Button>
            <Button type='submit' loading={sendMessage.isPending} disabled={sendMessage.isPending}>
              <Send className='srd:mr-2 srd:h-4 srd:w-4' />
              Kirim
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
